import { Injectable, inject } from '@angular/core'
import { ParamMap, Router } from '@angular/router'
import { Observable, map, of, switchMap } from 'rxjs'
import { OrderService } from './order/order.service'
import { SamlService } from './saml.service'
import { UserStateService } from '../store/state/user/user.state.service'
import { LiveLocationService } from './live-location.service'
import { LicOrderRequest, SamlResponse } from '../models/order/saml/saml.model'

export const PENDING_LAUNCH_ID_KEY = 'pending_launch_id'

@Injectable({ providedIn: 'root' })
export class LaunchAuthRedirectService {
  #orderService = inject(OrderService)
  #samlService = inject(SamlService)
  #router = inject(Router)
  #locationService = inject(LiveLocationService)
  #userStateService = inject(UserStateService)

  captureFromQueryParams(queryParamMap: ParamMap): string | null {
    const launchId = this.#getLaunchId(queryParamMap)
    if (launchId) {
      this.setPendingLaunchId(launchId)
    }
    return launchId
  }

  setPendingLaunchId(launchId: string | null | undefined): void {
    const normalizedLaunchId = launchId?.trim()
    if (!normalizedLaunchId) {
      return
    }
    sessionStorage.setItem(PENDING_LAUNCH_ID_KEY, normalizedLaunchId)
  }

  getPendingLaunchId(): string | null {
    return sessionStorage.getItem(PENDING_LAUNCH_ID_KEY)
  }

  clearPendingLaunchId(): void {
    sessionStorage.removeItem(PENDING_LAUNCH_ID_KEY)
  }

  handlePendingLaunchId(): Observable<number> {
    const launchId = this.getPendingLaunchId()
    if (!launchId) {
      return of(-1)
    }

    const device = this.#locationService.coordinates()
    const request = {
      UserId: this.#userStateService.getUser()?.UserId,
      Latitude: device?.latitude?.toString() ?? '0',
      Longitude: device?.longitude?.toString() ?? '0',
      GeoAccuracy: device?.accuracy
    } as LicOrderRequest

    this.clearPendingLaunchId()
    const launch$ = this.#orderService.processLaunchInContextId(launchId, request)
      .pipe(
        switchMap(response => this.#samlService.processSamlResponse(response)),
        map(response => { return this.determineRedirect(response) }))
    return launch$
  }

  private determineRedirect(samlResponse: SamlResponse): number {
    const accounts = samlResponse.ApiResponses.StartOrder.Accounts;
    const retailers = samlResponse.ApiResponses.StartOrder.Retailers;
    const salesPersons = samlResponse.ApiResponses.StartOrder.SalesPersons;

    if (accounts && accounts.length > 1 || retailers && retailers.length > 1 || salesPersons && salesPersons.length > 1) {
      return 1;
    }
    return samlResponse.ApiResponses?.MetaData?.NextPage ?? -1;
  }

  #getLaunchId(queryParamMap: ParamMap): string | null {
    return queryParamMap.get('launchId')
      ?? queryParamMap.get('launchid')
      ?? queryParamMap.get('launch_id')
      ?? queryParamMap.get('launch-id')
  }
}
